import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  DeleteSweep as ClearAllIcon,
  Cached as CacheIcon,
  Image as ImageIcon,
  ImageNotSupported as NoImageIcon,
  OpenInNew as OpenIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Schedule as ScheduleIcon,
  LocalOffer as TagIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
  Memory as MemoryIcon,
  Sync as SyncIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Domain as DomainIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, StatCard } from '../components/common';
import { adminApi } from '../utils/api';
import { format } from 'date-fns';

const Cache = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [qualityStats, setQualityStats] = useState(null);
  const [domainAnalytics, setDomainAnalytics] = useState([]);
  const [urlFailures, setUrlFailures] = useState(null);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, entriesRes, qualityRes, domainRes, failuresRes] = await Promise.all([
        adminApi.getCacheStats(),
        adminApi.getCacheEntries({ search, page: page + 1, limit: pageSize }),
        adminApi.getQualityStats().catch(() => ({ data: null })),
        adminApi.getDomainAnalytics({ limit: 10, sortBy: 'success_count' }).catch(() => ({ data: { domains: [] } })),
        adminApi.getUrlFailures().catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data.entries || []);
      setTotalCount(entriesRes.data.totalCount || 0);
      setQualityStats(qualityRes.data);
      setDomainAnalytics(domainRes.data?.domains || []);
      setUrlFailures(failuresRes.data);
    } catch (err) {
      console.error('Failed to fetch cache data:', err);
      // Show empty state on error - no mock data
      setStats(null);
      setQualityStats(null);
      setDomainAnalytics([]);
      setUrlFailures(null);
      setEntries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearSelected = async () => {
    try {
      for (const url of selected) {
        await adminApi.clearCacheEntry(url);
      }
      setSelected([]);
      fetchData();
      setClearDialogOpen(false);
    } catch (err) {
      console.error('Failed to clear cache entries:', err);
    }
  };

  const handleClearAll = async () => {
    try {
      await adminApi.clearAllCache();
      fetchData();
      setClearAllDialogOpen(false);
    } catch (err) {
      console.error('Failed to clear all cache:', err);
    }
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Recipe',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor: theme.palette.background.elevated,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {params.row.image_url ? (
              <Box
                component="img"
                src={params.row.image_url}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <NoImageIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
            )}
          </Box>
          <Box sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            {params.row.title}
          </Box>
        </Box>
      ),
    },
    {
      field: 'source_url',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.row.source_url}>
          <IconButton
            size="small"
            href={params.row.source_url}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
          {(params.row.tags || []).slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                backgroundColor: `${theme.palette.primary.main}15`,
              }}
            />
          ))}
          {(params.row.tags?.length || 0) > 2 && (
            <Chip
              label={`+${params.row.tags.length - 2}`}
              size="small"
              sx={{ height: 22, fontSize: '0.68rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'quality_score',
      headerName: 'Quality',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const score = params.row.quality_score;
        if (score === null || score === undefined) {
          return <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>-</Typography>;
        }
        const color = score >= 70 ? 'success.main' : score >= 40 ? 'warning.main' : 'error.main';
        return (
          <Chip
            label={score}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: `${theme.palette[color.split('.')[0]][color.split('.')[1]]}20`,
              color: color,
            }}
          />
        );
      },
    },
    {
      field: 'access_count',
      headerName: 'Hits',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'last_accessed_at',
      headerName: 'Last Accessed',
      width: 140,
      renderCell: (params) =>
        params.row.last_accessed_at
          ? format(new Date(params.row.last_accessed_at), 'MMM d, h:mm a')
          : '-',
    },
    {
      field: 'created_at',
      headerName: 'Cached',
      width: 120,
      renderCell: (params) =>
        params.row.created_at
          ? format(new Date(params.row.created_at), 'MMM d, yyyy')
          : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Remove from cache">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelected([params.row.source_url]);
              setClearDialogOpen(true);
            }}
            sx={{ color: theme.palette.text.secondary }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="web recipe cache"
        subtitle="manage cached web recipes"
        breadcrumbs={[{ label: 'cache' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selected.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setClearDialogOpen(true)}
              >
                Clear ({selected.length})
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => setClearAllDialogOpen(true)}
            >
              Clear All
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* Redis Cache Stats */}
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
        Redis Cache (Fast)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Status"
            value={stats?.redis?.available ? 'Connected' : 'Offline'}
            icon={stats?.redis?.available ? CloudIcon : CloudOffIcon}
            color={stats?.redis?.available ? 'success' : 'error'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Cached Recipes"
            value={stats?.redis?.recipeCount}
            icon={CacheIcon}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Processing"
            value={stats?.redis?.processingCount}
            icon={SyncIcon}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Errors"
            value={stats?.redis?.errorCount}
            icon={ErrorIcon}
            color={stats?.redis?.errorCount > 0 ? 'warning' : 'success'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Memory"
            value={stats?.redis?.memoryUsed || '-'}
            icon={MemoryIcon}
            color="secondary"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Database Cache Stats */}
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
        Database Cache (Persistent)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Total Entries"
            value={stats?.database?.totalEntries ?? stats?.totalEntries}
            icon={StorageIcon}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Cache Size"
            value={stats?.database?.totalSize ?? stats?.totalSize}
            icon={StorageIcon}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Avg Age"
            value={stats?.database?.avgAge ?? stats?.avgAge}
            icon={ScheduleIcon}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="With Images"
            value={stats?.database?.withImages ?? stats?.withImages}
            icon={ImageIcon}
            color="secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="With Tags"
            value={stats?.database?.withTags ?? stats?.withTags}
            icon={TagIcon}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Total Accesses"
            value={stats?.database?.totalAccesses}
            icon={SpeedIcon}
            color="success"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Quality Metrics */}
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
        Quality Metrics
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Avg Quality Score"
            value={qualityStats?.avgQualityScore ?? '-'}
            icon={AssessmentIcon}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="High Quality (70+)"
            value={qualityStats?.distribution?.high ?? 0}
            icon={TrendingUpIcon}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Medium (40-69)"
            value={qualityStats?.distribution?.medium ?? 0}
            icon={CheckCircleIcon}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Low Quality (<40)"
            value={qualityStats?.distribution?.low ?? 0}
            icon={TrendingDownIcon}
            color="error"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Unique Domains"
            value={qualityStats?.domainCount ?? 0}
            icon={DomainIcon}
            color="info"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* URL Failure Stats */}
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
        URL Processing Failures
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Failures"
            value={urlFailures?.total ?? 0}
            icon={ErrorIcon}
            color="error"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Active Cooldowns"
            value={urlFailures?.activeCooldowns ?? 0}
            icon={BlockIcon}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Low Quality"
            value={urlFailures?.byReason?.low_quality ?? 0}
            icon={TrendingDownIcon}
            color="error"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Fetch Errors"
            value={urlFailures?.byReason?.fetch_error ?? 0}
            icon={WarningIcon}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Listicles Blocked"
            value={urlFailures?.byReason?.listicle ?? 0}
            icon={BlockIcon}
            color="info"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Top Domains */}
      {domainAnalytics.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
            Top Performing Domains
          </Typography>
          <Box sx={{ mb: 4, overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {domainAnalytics.slice(0, 10).map((domain) => (
                <Chip
                  key={domain.domain}
                  label={`${domain.domain} (${domain.success_rate}% • ${domain.success_count} recipes)`}
                  size="small"
                  sx={{
                    backgroundColor: domain.success_rate >= 70 
                      ? `${theme.palette.success.main}15`
                      : domain.success_rate >= 40
                        ? `${theme.palette.warning.main}15`
                        : `${theme.palette.error.main}15`,
                    color: domain.success_rate >= 70 
                      ? 'success.main'
                      : domain.success_rate >= 40
                        ? 'warning.main'
                        : 'error.main',
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search cached recipes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Data Table */}
      <DataTable
        rows={entries}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        checkboxSelection
        onSelectionChange={(ids) => {
          const urls = entries.filter((e) => ids.includes(e.id)).map((e) => e.source_url);
          setSelected(urls);
        }}
        getRowId={(row) => row.id}
      />

      {/* Clear Selected Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Clear Cache Entries</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove the selected entries from the cache. They will be re-cached on next access.
          </Alert>
          Are you sure you want to clear {selected.length} cache entry(s)?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClearSelected}>
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)}>
        <DialogTitle>Clear Entire Cache</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will remove ALL cached recipes. This action cannot be undone.
          </Alert>
          Are you sure you want to clear the entire cache ({stats?.totalEntries} entries)?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClearAll}>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cache;
